import role15MfrsSchema from '../../../../assets/forms/role-410000-mfrs.form.json';
import role15MpersSchema from '../../../../assets/forms/role-410000-mpers.form.json';
import { pickSchema, MPERS_ACCOUNTING_STANDARD } from './schema-picker.util';
import { isEmptyCell } from '../validation-field-key.util';

/**
 * Shared business rules for Role 15 (Statement of Comprehensive Income).
 *
 * Role 15 ships TWO form JSONs (role-410000-mfrs.form.json and
 * role-410000-mpers.form.json) that differ in qname prefix
 * (ifrs-full:* vs ifrs-smes:* and ssmt-mfrs:* vs ssmt-mpers:*) and a few
 * MFRS-only OCI categories. The component picks between them at mount time
 * based on the filing's accounting standard via CrossReferenceService.isMFRS;
 * the pure validator has to make the same selection from
 * SheetValidationContext.accountingStandard.
 *
 * Both the editor's role15-410000-statement-of-comprehensive-income.component.ts
 * AND role15StatementOfComprehensiveIncomeValidator import from this file so
 * the selection literal and cross-sheet key derivation live in exactly one
 * place. If the backend ever renames the accounting-standard enum, only this
 * file changes — the parity-check harness will catch any consumer that drifted.
 *
 * ---- Cross-sheet dependency surface --------------------------------------
 *
 * Role 15 reads two slices for its OWN validation:
 *   1. `filingPrerequisites` — drives:
 *        - `NatureOfFinancialStatements` ("Consolidated" / "Separate") which
 *          (a) toggles whether Consolidated columns are visible at all and
 *          (b) makes the two `Comprehensive income, attributable to ...`
 *          rows mandatory via the schema's `requiredIf` — see
 *          {@link Role15RequiredIfRule}.
 *        - `DisclosureOfFinancialStatementsPreparationForCurrentSubmission`
 *          which decides whether PreviousYear columns are visible.
 *   2. `statementOfChangesInEquity` (Role 19) — provides the equity totals
 *      that Role 15's `ComprehensiveIncome` and `OtherComprehensiveIncome`
 *      values are compared against. Mismatches generate "Failed" status on
 *      those rows. Role 19 is also Role 15's own downstream (per
 *      SheetDependencyService) so the cycle is by design.
 *
 * `accountingStandard` arrives via context.accountingStandard (filingInfo's
 * BasisOfAccountingStandardsAppliedToPrepareFinancialStatements field — same
 * concept exposed under filingInfo for the orchestrator and under
 * filingPrerequisites for the editor's CrossReferenceService).
 */

/**
 * Literal value of
 * filingInfo['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements']
 * that selects MPERS. Any other value (including undefined, '', or
 * 'Malaysian Financial Reporting Standards') falls back to MFRS, mirroring
 * the component's `isMFRS ? mfrs-file : mpers-file` ternary which defaults to
 * the MFRS schema whenever the cross-reference resolver does NOT report
 * MFRS — i.e. MPERS is the explicit "not-MFRS" branch for this sheet.
 *
 * The literal now lives in ONE place — `MPERS_ACCOUNTING_STANDARD` in
 * `schema-picker.util.ts` (the plural "Standards" form the backend emits) — and
 * every role re-exports it. A previous singular "Entity" spelling in
 * cross-reference.service.ts never matched the backend and has been removed.
 */
export const ROLE15_MPERS_ACCOUNTING_STANDARD = MPERS_ACCOUNTING_STANDARD;

/** Filename of the MFRS (default) variant schema. */
export const ROLE15_MFRS_SCHEMA_FILE = 'role-410000-mfrs.form.json';

/** Filename of the MPERS variant schema. */
export const ROLE15_MPERS_SCHEMA_FILE = 'role-410000-mpers.form.json';

/**
 * Column-visibility gating for Role 15 now lives in the shared, config-driven
 * resolver: see `dimension-visibility.util.ts` (`resolveDimensionVisibility`)
 * and the central `dimension-visibility-registry.ts`
 * (`SHEET_KEYS.statementOfComprehensiveIncome` → NATURE / PREP gates). Both the
 * mounted editor (via `CrossReferenceService.getVisibility`) and the pure
 * `role15StatementOfComprehensiveIncomeValidator` call that one resolver, so the
 * former role-local `ROLE15_NATURE_*` / `ROLE15_PREPARATION_*` constants,
 * `Role15ColumnVisibility`, `resolveRole15PrerequisiteValue`, and
 * `resolveRole15ColumnVisibility` were removed to keep a single source.
 */

export type Role15Dimension = 'Consolidated' | 'Separate';
export type Role15Period = 'CurrentYear' | 'PreviousYear';

/**
 * Minimal structural view of a role 15 schema. Only what the pure validator
 * reads is typed — the shipped JSON is far wider. `ui.linkTo` is present
 * because `Profit (loss)` inherits its value from Role 12 (see
 * {@link pickRole15Schema}); `applyLinkedInheritance` matches on `linkTo.role`.
 */
export interface Role15SchemaControl {
  qname: string;
  label?: string;
  ui?: {
    linkTo?: { role: string; qname: string; navigate?: boolean };
    [key: string]: unknown;
  };
  requiredIf?: Role15RequiredIfRule;
  [key: string]: unknown;
}

/**
 * Cross-sheet conditional-required rule as declared in the role 15 schemas.
 *
 * Only the two attribution rows carry one
 * (`ComprehensiveIncomeAttributableToOwnersOfParent` and
 * `...NoncontrollingInterests`, in BOTH the MFRS and MPERS variants): when
 * `filingPrerequisites.NatureOfFinancialStatements` is `"Consolidated"`, every
 * VISIBLE column of those rows must carry a value.
 *
 * This is the CROSS-SHEET spelling of `requiredIf` -- `{ sheet, field, equals }`,
 * the same shape role 12 uses and one of the six declaration forms the shared
 * `isControlMandatory` (`required-field.util.ts`) understands, so the label's
 * `*` marker follows from the same declaration that drives the blocking error.
 *
 * `errorMessage` is the P.O.'s exact copy and is asserted character-for-character
 * by the validator spec -- do not reword it.
 *
 * _History:_ these blocks were stripped on 2026-06-03 (the rows were made
 * unconditionally optional) and REINSTATED on 2026-08-21 after the P.O.
 * re-checked the source sheets. Both the removal and the reinstatement covered
 * MFRS and MPERS together.
 */
export interface Role15RequiredIfRule {
  sheet: string;
  field: string;
  equals: string;
  errorMessage?: string;
}

export interface Role15Schema {
  controls: Role15SchemaControl[];
  [key: string]: unknown;
}

/**
 * Resolve the correct role 15 schema filename for the given accounting
 * standard. Mirrors the component's loadSchema() ternary so both paths
 * derive from the same rule.
 *
 * Selection direction is the standard MFRS-default one (MPERS on exact-match
 * against the shared MPERS literal, MFRS as the fallback). The shared
 * `pickSchema(std, mfrs, mpers)` returns `mpers` only when `std` equals the
 * shared `MPERS_ACCOUNTING_STANDARD`, so we pass the MFRS filename first and
 * the MPERS filename second. Behaviour is identical to the previous
 * `std === MPERS_LITERAL ? MPERS_FILE : MFRS_FILE` ternary.
 */
export function pickRole15SchemaFile(
  accountingStandard: string | undefined | null
): string {
  return pickSchema(
    accountingStandard,
    ROLE15_MFRS_SCHEMA_FILE,
    ROLE15_MPERS_SCHEMA_FILE
  );
}

/**
 * Resolve the actual role 15 schema OBJECT for the given accounting standard.
 *
 * Mirrors `pickRole12Schema` / `pickRole19Schema`: the pure validator cannot
 * `fetch()` at validate time (the listing's cache-miss path runs
 * synchronously), so both variants are imported eagerly and selected from
 * memory. Needed since Role 15's `Profit (loss)` became a `ui.linkTo` leaf —
 * `applyLinkedInheritance` reads the control list to find it.
 *
 * Costs no extra bundle weight: `field-link-label.util.ts` already imports both
 * role-410000 JSONs statically.
 */
export function pickRole15Schema(
  accountingStandard: string | undefined | null
): Role15Schema {
  return pickSchema(
    accountingStandard,
    role15MfrsSchema as unknown as Role15Schema,
    role15MpersSchema as unknown as Role15Schema
  );
}

/** Returns true when the accounting standard resolves to MFRS. */
export function isRole15MFRS(
  accountingStandard: string | undefined | null
): boolean {
  return accountingStandard !== ROLE15_MPERS_ACCOUNTING_STANDARD;
}

/**
 * Build the bracket-notation form key the component uses for a row+column
 * cell: `qname[Consolidated|Separate][CurrentYear|PreviousYear]`. Mirrors
 * the component's getXbrlControlName() helper.
 */
export function buildRole15ColumnKey(
  qname: string,
  dimension: Role15Dimension,
  period: Role15Period
): string {
  return `${qname}[${dimension}][${period}]`;
}

/**
 * Check if a single form-shaped value counts as "empty" for the purposes of
 * the cross-sheet equality checks. Matches the component's
 *   formControl.value === null
 * guard — the editor intentionally treats only the literal `null` as missing
 * (zero is a valid monetary value), so we mirror that here. We also accept
 * undefined and '' since orchestrator-loaded payloads may surface either when
 * the form control was never persisted.
 */
export function isRole15Empty(value: unknown): boolean {
  return isEmptyCell(value);
}

/**
 * The two top-level totals Role 15 cross-validates against Role 19 (SOCIE).
 * Returned as MFRS / MPERS prefixed pairs because the component's
 * validateComprehensiveIncomeAgainstRole19() picks the prefix at runtime
 * based on isMFRS().
 */
export interface Role15CrossSheetTargets {
  comprehensiveIncomeQname: string;
  otherComprehensiveIncomeQname: string;
}

export function getRole15CrossSheetTargets(
  accountingStandard: string | undefined | null
): Role15CrossSheetTargets {
  const mfrs = isRole15MFRS(accountingStandard);
  return {
    comprehensiveIncomeQname: mfrs
      ? 'ifrs-full:ComprehensiveIncome'
      : 'ifrs-smes:ComprehensiveIncome',
    otherComprehensiveIncomeQname: mfrs
      ? 'ifrs-full:OtherComprehensiveIncome'
      : 'ifrs-smes:OtherComprehensiveIncome',
  };
}

/**
 * Build the candidate Role 19 lookup keys for a given Role 15 base qname,
 * dimension, and period. Role 19 stores its data with an equity-axis suffix
 * that varies by template (EquityMember vs EquityAttributableToOwnersOfParentMember
 * for consolidated parent attribution); the component's
 * validateComprehensiveIncomeAgainstRole19() probes both formats plus a
 * legacy dual-column fallback. Order matters: first match wins.
 */
export function buildRole19CandidateKeys(
  baseQname: string,
  dimension: Role15Dimension,
  period: Role15Period,
  accountingStandard: string | undefined | null
): string[] {
  const mfrs = isRole15MFRS(accountingStandard);
  const equityMember = mfrs ? 'ifrs-full:EquityMember' : 'ifrs-smes:EquityMember';
  const equityAttributable = mfrs
    ? 'ifrs-full:EquityAttributableToOwnersOfParentMember'
    : 'ifrs-smes:EquityAttributableToOwnersOfParentMember';

  return [
    `${baseQname}:${equityMember}[${dimension}][${period}]`,
    `${baseQname}:${equityAttributable}[${dimension}][${period}]`,
    // Legacy dual-column fallback the editor still probes for back-compat.
    `${baseQname}[${dimension}][${period}]`,
  ];
}

